/**
 * Live transcription over the OpenAI Realtime API (WebRTC).
 *
 * The server mints a short-lived client secret for the business's OpenAI key;
 * the browser then streams the mic straight to OpenAI and receives transcript
 * deltas as the user speaks. The caller decides when the turn ends (its own
 * silence detection) and calls finish(), which commits the audio buffer and
 * waits for the final transcript.
 */
import { API_BASE_URL } from '../config/api';
import { isVoiceRealtimeTranscriptionDisabled } from './killSwitches';

const REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';
/** Lets the last spoken audio reach OpenAI over RTP before the commit arrives. */
const COMMIT_DELAY_MS = 250;
const FINAL_TRANSCRIPT_TIMEOUT_MS = 4000;
/** After "no OpenAI key" style answers, stop asking the server for a while. */
const UNAVAILABLE_RETRY_MS = 5 * 60 * 1000;

let unavailableUntil = 0;

export interface RealtimeTranscriptionSession {
  /** When the data channel opened — audio before this was not transcribed. */
  readonly connectedAt: number;
  /** Commit the turn and resolve with the final transcript, or '' if none arrived. */
  finish(): Promise<string>;
  /** Tear down without waiting for a transcript. Safe to call more than once. */
  close(): void;
}

export interface StartRealtimeTranscriptionOptions {
  language: string;
  /** Running transcript (finished turns plus the in-progress one). */
  onTranscript: (text: string) => void;
  /** Give up connecting after this long and resolve null. */
  timeoutMs: number;
}

export function supportsRealtimeTranscription(): boolean {
  if (typeof window === 'undefined') return false;
  if (isVoiceRealtimeTranscriptionDisabled()) return false;
  if (Date.now() < unavailableUntil) return false;
  return typeof RTCPeerConnection !== 'undefined';
}

const getAuthToken = (): string | null =>
  localStorage.getItem('token') || localStorage.getItem('auth_token');

async function requestClientSecret(language: string): Promise<string | null> {
  const token = getAuthToken();
  if (!token) return null;
  const businessId = localStorage.getItem('businessId');
  const query = businessId ? `?business_id=${encodeURIComponent(businessId)}` : '';

  const response = await fetch(`${API_BASE_URL}/voice/realtime-session${query}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ language }),
  });
  const data = await response.json().catch(() => ({} as Record<string, unknown>));

  if (!response.ok) {
    // No key / disabled / endpoint missing: use recorded uploads for a while.
    if (response.status === 503 || response.status === 404) {
      unavailableUntil = Date.now() + UNAVAILABLE_RETRY_MS;
    }
    console.info('Live transcription unavailable:', data?.error || response.status);
    return null;
  }
  return typeof data.client_secret === 'string' ? data.client_secret : null;
}

type RealtimeServerEvent = {
  type?: string;
  item_id?: string;
  delta?: string;
  transcript?: string;
  error?: { message?: string; code?: string };
};

/**
 * Open a live transcription session on (a clone of) the stream's audio track.
 * Resolves null when live transcription is unavailable or does not connect in time;
 * callers then fall back to recorded-upload transcription.
 */
export async function startRealtimeTranscription(
  stream: MediaStream,
  { language, onTranscript, timeoutMs }: StartRealtimeTranscriptionOptions
): Promise<RealtimeTranscriptionSession | null> {
  const sourceTrack = stream.getAudioTracks()[0];
  if (!sourceTrack || !supportsRealtimeTranscription()) return null;

  // Own a clone so the caller can release its stream while the final commit is in flight.
  const track = sourceTrack.clone();
  const pc = new RTCPeerConnection();
  const channel = pc.createDataChannel('oai-events');
  let closed = false;
  /** Set on a server error or dropped connection: finish() then returns '' at once. */
  let failed = false;

  /** Transcript text per item, in the order items started. */
  const items = new Map<string, { text: string; final: boolean }>();
  let onFinalTranscript: (() => void) | null = null;
  let onFatalError: (() => void) | null = null;

  const runningTranscript = () => Array.from(items.values())
    .map((item) => item.text.trim())
    .filter(Boolean)
    .join(' ');

  const close = () => {
    if (closed) return;
    closed = true;
    try {
      channel.close();
    } catch {
      /* ignore */
    }
    track.stop();
    pc.close();
  };

  channel.addEventListener('message', (message) => {
    let event: RealtimeServerEvent;
    try {
      event = JSON.parse(message.data);
    } catch {
      return;
    }
    const itemId = event.item_id || 'default';

    if (event.type === 'conversation.item.input_audio_transcription.delta' && event.delta) {
      const item = items.get(itemId) || { text: '', final: false };
      item.text += event.delta;
      items.set(itemId, item);
      onTranscript(runningTranscript());
    } else if (event.type === 'conversation.item.input_audio_transcription.completed') {
      items.set(itemId, { text: event.transcript || '', final: true });
      onTranscript(runningTranscript());
      onFinalTranscript?.();
    } else if (event.type === 'error') {
      console.warn('Live transcription error:', event.error?.code, event.error?.message);
      failed = true;
      onFatalError?.();
    }
  });

  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
      failed = true;
      onFatalError?.();
    }
  });

  const connect = async (): Promise<number> => {
    const clientSecret = await requestClientSecret(language);
    if (!clientSecret || closed) throw new Error('No client secret');

    pc.addTrack(track, new MediaStream([track]));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const answer = await fetch(REALTIME_CALLS_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    });
    if (!answer.ok) {
      throw new Error(`Realtime call rejected (${answer.status})`);
    }
    await pc.setRemoteDescription({ type: 'answer', sdp: await answer.text() });

    if (channel.readyState !== 'open') {
      await new Promise<void>((resolve, reject) => {
        channel.addEventListener('open', () => resolve(), { once: true });
        channel.addEventListener('error', () => reject(new Error('Data channel failed')), { once: true });
      });
    }
    return Date.now();
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  let connectedAt: number;
  try {
    connectedAt = await Promise.race([
      connect(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Live transcription connect timed out')), timeoutMs);
      }),
    ]);
  } catch (error) {
    console.info('Live transcription not started:', error instanceof Error ? error.message : error);
    close();
    return null;
  } finally {
    clearTimeout(timer);
  }

  const finish = async (): Promise<string> => {
    if (closed) return '';
    await new Promise((resolve) => setTimeout(resolve, COMMIT_DELAY_MS));
    if (closed || failed || channel.readyState !== 'open') {
      close();
      return '';
    }

    const pendingItems = () => Array.from(items.values()).some((item) => !item.final);
    const finalReceived = new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), FINAL_TRANSCRIPT_TIMEOUT_MS);
      const settle = (ok: boolean) => {
        clearTimeout(timeout);
        resolve(ok);
      };
      onFinalTranscript = () => {
        if (!pendingItems()) settle(true);
      };
      onFatalError = () => settle(false);
    });

    try {
      channel.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
    } catch {
      close();
      return '';
    }
    const ok = await finalReceived;
    const transcript = runningTranscript();
    close();
    return ok ? transcript : '';
  };

  return { connectedAt, finish, close };
}

/** Test hook: forget a cached "unavailable" answer. */
export function resetRealtimeTranscriptionAvailability(): void {
  unavailableUntil = 0;
}
